import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Loader2, PlaneTakeoff, CalendarRange, BadgeCheck, Plus } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Skeleton } from '@/app/components/ui/skeleton';
import { TravelItineraryCards } from '@/app/components/travel/travel-itinerary';
import { TravelItineraryEditor } from './travel-itinerary-editor';
import { useMyTravel, useConfirmMyTravel } from '@/hooks/use-my-travel';
import { useTravelItineraries, useCreateTravelItinerary } from '@/hooks/use-crew';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';
import type { TravelItinerary } from '@/lib/types';
import { toast } from '@/app/components/ui/simple-toaster';
import { usePermissions } from '@/hooks/use-permissions';

function statusBadge(itinerary: TravelItinerary) {
  return itinerary.is_confirmed ? (
    <Badge className="bg-emerald-100 text-emerald-800">Confirmed</Badge>
  ) : (
    <Badge className="bg-amber-100 text-amber-800">Unconfirmed</Badge>
  );
}

export function TravelItineraryPage() {
  const { canManageOnboarding: canManageCrew } = usePermissions();
  const timeZone = useOrgTimeZone();
  const [searchParams] = useSearchParams();
  const assignmentId = Number(searchParams.get('assignment') ?? 0);
  const { data: myTravel, isLoading: myTravelLoading } = useMyTravel();
  const { data: coordinatorTravel, isLoading: coordinatorTravelLoading } = useTravelItineraries(
    assignmentId ? { assignment: assignmentId, page_size: 20 } : {}
  );
  const confirmTravel = useConfirmMyTravel();
  const createItinerary = useCreateTravelItinerary();

  const hasAssignment = assignmentId > 0;
  const myItinerary = useMemo(
    () => myTravel?.find((itinerary) => itinerary.assignment === assignmentId) ?? null,
    [assignmentId, myTravel]
  );
  const coordinatorItinerary = coordinatorTravel?.results?.[0] ?? null;
  const activeItinerary = myItinerary ?? coordinatorItinerary ?? myTravel?.[0] ?? null;
  const isTechView = hasAssignment ? Boolean(myItinerary) : true;
  const loading = myTravelLoading || coordinatorTravelLoading;

  const handleConfirm = async (id: number) => {
    try {
      await confirmTravel.mutateAsync(id);
      toast.success('Travel confirmed — thank you.');
    } catch {
      toast.error('Failed to confirm travel.');
    }
  };

  const handleCreateItinerary = async () => {
    if (!assignmentId) return;
    try {
      await createItinerary.mutateAsync({ assignment: assignmentId });
      toast.success('Travel itinerary created.');
    } catch {
      toast.error('Failed to create travel itinerary.');
    }
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-10 w-56" />
        <Skeleton className="h-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (!assignmentId && (!myTravel || myTravel.length === 0)) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card>
          <CardContent className="py-12 text-center">
            <PlaneTakeoff className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-3 text-sm text-slate-500">No travel itineraries available.</p>
            <Button asChild variant="outline" className="mt-4">
              <Link to="/my-dashboard">Back to dashboard</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-5xl mx-auto">
      <div className="flex flex-wrap items-center gap-3">
        <Button asChild variant="ghost" size="sm" className="pl-0">
          <Link to={isTechView ? '/my-dashboard' : '/crew/techs'}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Link>
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">Travel Itinerary</h1>
          <p className="text-sm text-slate-500">
            {isTechView
              ? 'Review your itinerary and confirm once your plan is ready.'
              : 'Coordinate travel segments and keep the tech confirmation in sync.'}
          </p>
        </div>
      </div>

      {activeItinerary ? (
        <>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <CalendarRange className="h-5 w-5 text-slate-500" />
                Itinerary status
              </CardTitle>
              <CardDescription>
                {activeItinerary.project_name} · {activeItinerary.tech_name}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                {statusBadge(activeItinerary)}
                <span className="text-sm text-slate-500">
                  {activeItinerary.is_confirmed && activeItinerary.acknowledged_at
                    ? `Confirmed ${formatDateTimeInTimeZone(activeItinerary.acknowledged_at, timeZone)}`
                    : 'Needs confirmation'}
                </span>
              </div>
              {isTechView && !activeItinerary.is_confirmed && (
                <Button
                  size="lg"
                  disabled={confirmTravel.isPending}
                  onClick={() => handleConfirm(activeItinerary.id)}
                  className="min-h-11"
                >
                  {confirmTravel.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <BadgeCheck className="mr-2 h-4 w-4" />
                  )}
                  Confirm — I have my travel
                </Button>
              )}
              {isTechView && activeItinerary.is_confirmed && activeItinerary.acknowledged_at && (
                <p className="text-sm text-slate-600">
                  Confirmed at {formatDateTimeInTimeZone(activeItinerary.acknowledged_at, timeZone)}.
                </p>
              )}
            </CardContent>
          </Card>

          <TravelItineraryCards itinerary={activeItinerary} />

          {!isTechView && assignmentId > 0 && (
            <TravelItineraryEditor itinerary={activeItinerary} assignmentId={assignmentId} />
          )}
        </>
      ) : (
        <Card>
          <CardContent className="py-12 text-center text-sm text-slate-500">
            <p>No itinerary found for this assignment.</p>
            {assignmentId > 0 && !isTechView && canManageCrew && (
              <Button
                className="mt-4"
                disabled={createItinerary.isPending}
                onClick={handleCreateItinerary}
              >
                {createItinerary.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="mr-2 h-4 w-4" />
                )}
                Create itinerary
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {!assignmentId && myTravel && myTravel.length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PlaneTakeoff className="h-5 w-5 text-slate-500" />
              Your itineraries
            </CardTitle>
            <CardDescription>Select an itinerary to review or confirm.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {myTravel.map((itinerary) => (
              <Link
                key={itinerary.id}
                to={`/my-dashboard/travel/${itinerary.id}`}
                className="flex items-center justify-between gap-3 rounded-xl border bg-white p-4 hover:bg-slate-50"
              >
                <div className="min-w-0">
                  <p className="font-medium text-slate-900 truncate">{itinerary.project_name}</p>
                  <p className="text-sm text-slate-500 truncate">{itinerary.tech_name}</p>
                </div>
                {statusBadge(itinerary)}
              </Link>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
